import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconHourglass, IconClose, IconMailOpen, IconSend } from '../../icons';
import logger from '../../utils/logger';
import { formatDate } from '../../utils/formatters';
import {
  listMyClientInvites,
  inviteClientByEmail,
  type SentClientInvite,
} from '../../api/clientInviteApi';

/**
 * WL-K5a — Gesendete Mandanten-Einladungen (Kanzlei-Sicht).
 *
 * Zeigt der Kanzlei die versendeten Einladungen mit Status (Eingeladen / Angenommen
 * / Abgelehnt / Abgelaufen) und — ehrlich getrennt — ob die Mail bereits ZUGESTELLT
 * wurde („eingeladen" ≠ „zugestellt": `mailSent===false` heißt der Versand steht
 * noch aus). Für solche Einträge bietet das Panel einen „Erneut senden"-Button, der
 * die Einladung neu anstößt (Resend-Recovery bei `mailSentAt:null`).
 *
 * Liest die Daten serverseitig über {@link listMyClientInvites} — `client_invites`
 * ist clientseitig via Rules nicht lesbar (server-only). Der `tokenHash` verlässt
 * den Server nie.
 *
 * @module SentClientInvitesPanel
 */
interface SentClientInvitesPanelProps {
  /** Incrementing key: bei Änderung lädt das Panel die Liste neu. */
  refreshKey: number;
}

/** Maps a server status to its i18n key. */
function statusKey(status: SentClientInvite['status']): string {
  switch (status) {
    case 'accepted':
      return 'clientInvite.sent.statusAccepted';
    case 'declined':
      return 'clientInvite.sent.statusDeclined';
    case 'expired':
      return 'clientInvite.sent.statusExpired';
    default:
      return 'clientInvite.sent.statusInvited';
  }
}

/** Tailwind color class for a status badge. */
function statusColor(status: SentClientInvite['status']): string {
  switch (status) {
    case 'accepted':
      return 'text-[var(--tone-success-fg)] bg-[var(--tone-success-bg)]';
    case 'declined':
      return 'text-[var(--tone-danger-fg)] bg-[var(--tone-danger-bg)]';
    case 'expired':
      return 'text-muted bg-surface-inset';
    default:
      return 'text-[var(--tone-warning-fg)] bg-[var(--tone-warning-bg)]';
  }
}

/** Icon for a status badge. */
function StatusIcon({ status }: { status: SentClientInvite['status'] }) {
  switch (status) {
    case 'accepted':
      return <IconCheck size={12} className="mr-1 inline" />;
    case 'declined':
      return <IconClose size={12} className="mr-1 inline" />;
    default:
      return <IconHourglass size={12} className="mr-1 inline" />;
  }
}

const SentClientInvitesPanel: React.FC<SentClientInvitesPanelProps> = ({ refreshKey }) => {
  const { t, i18n } = useTranslation('common');

  const [invites, setInvites] = useState<SentClientInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listMyClientInvites();
      setInvites(result.invites);
    } catch (err) {
      logger.error('[SentClientInvitesPanel] load failed', err);
      setError(t('clientInvite.sent.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void load(); }, [refreshKey, load]);

  const handleResend = async (email: string | null) => {
    if (!email) return;
    setResendingEmail(email);
    try {
      // Re-Invite derselben (bereits bestätigten) Einladung; der Server regeneriert
      // den Token nur, solange mailSentAt null ist (Resend-Recovery), sonst No-Op.
      await inviteClientByEmail(email, true);
      await load();
    } catch (err) {
      logger.error('[SentClientInvitesPanel] resend failed', err);
      setError(t('clientInvite.sent.error'));
    } finally {
      setResendingEmail(null);
    }
  };

  return (
    <section className="mt-4 rounded-lg bg-surface-raised ring-1 ring-black ring-opacity-5">
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        <IconMailOpen className="text-accent-ink shrink-0" />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
          {t('clientInvite.sent.title')}
        </h2>
      </div>

      {loading && <p className="px-4 pb-4 text-sm text-faint">{t('clientInvite.sent.loading')}</p>}
      {!loading && error && <p className="px-4 pb-4 text-sm text-[var(--tone-danger-fg)]">{error}</p>}
      {!loading && !error && invites.length === 0 && (
        <p className="px-4 pb-4 text-sm text-faint">{t('clientInvite.sent.empty')}</p>
      )}

      {!loading && !error && invites.length > 0 && (
        <ul className="divide-y divide-[color:var(--border-hairline)]">
          {invites.map((invite, idx) => {
            // Null-Check auf der ROHEN ms-Quelle, nicht auf dem formatierten String —
            // der zentrale `formatDate` liefert für fehlendes Datum bereits '—', nie `null`.
            const sentDate = invite.invitedAtMs != null ? formatDate(invite.invitedAtMs, { locale: i18n.language }) : null;
            const showResend = invite.status === 'invited' && !invite.mailSent;
            return (
              <li key={`${invite.email ?? 'unknown'}-${idx}`} className="px-4 py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-secondary truncate">
                    {invite.clientName ? `${invite.clientName} — ` : ''}
                    {invite.email ?? <span className="italic text-faint">{t('clientInvite.unknownRecipient')}</span>}
                  </p>
                  {sentDate && (
                    <p className="text-xs text-faint mt-0.5">
                      {t('clientInvite.sent.invitedOn', { date: sentDate })}
                    </p>
                  )}
                  {invite.status === 'invited' && !invite.mailSent && (
                    <p className="text-xs text-yellow-500/80 mt-0.5">{t('clientInvite.sent.notDelivered')}</p>
                  )}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(invite.status)}`}>
                    <StatusIcon status={invite.status} />
                    {t(statusKey(invite.status))}
                  </span>
                  {showResend && (
                    <button
                      type="button"
                      onClick={() => handleResend(invite.email)}
                      disabled={resendingEmail === invite.email}
                      className="inline-flex items-center gap-1 rounded-md bg-surface-inset px-2 py-0.5 text-[11px] font-medium text-secondary hover:bg-surface-hover disabled:opacity-50"
                    >
                      <IconSend size={10} />
                      {resendingEmail === invite.email ? t('clientInvite.working') : t('clientInvite.sent.resend')}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default SentClientInvitesPanel;
